import React, { useContext, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  black,
  errorColor,
  gray100,
  primaryButtonBackgroundColor
} from '../../../constants/Colors';
import { showMessageEnum, userType } from '../../../constants/Enum';
import { sideBarWidth, spHeaderHeight } from '../../../constants/Location';
import { AuthContext } from '../../../contexts/auth';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomTextfield from '../../uiParts/CustomTextfield';
import CustomSelectField from '../../uiParts/CustomSelectField';
import { MaterialType } from '../../../validation/materials/materialType';
import { materialSchema } from '../../../validation/materials';
import { useLocation } from 'react-router-dom';
import { getCategoryList } from '../../../api/db/category';
import { common } from '../../../modules/error';
import { AxiosError } from 'axios';
import { response } from '../../../api/db';
import { createMaterial, updateMaterial } from '../../../api/db/material';
import SideBar from '../../uiParts/SideBar';
import { sentryLog } from '../../../config/sentry';
import CustomCheckBox from '../../uiParts/CustomCheckBox';

const styles = {
  container: {
    '@media screen and (min-width:961px)': {
      ml: sideBarWidth
    },
    '@media screen and (max-width:600px)': {
      p: '24px 16px'
    },
    '@media screen and (max-width:1440px)': {
      flexDirection: 'column'
    },
    '@media screen and (min-width:1441px)': {
      flexDirection: 'row'
    },
    '@media screen and (max-width:961px)': {
      mt: spHeaderHeight,
      maxWidth: '100%'
    },
    p: '32px',
    maxWidth: '560px'
  },
  body: {
    '@media screen and (max-width:600px)': {
      minWidth: '340px'
    },
    '@media screen and (min-width:1441px)': {
      mr: '56px'
    }
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '24px'
    },
    color: gray100,
    fontSize: '32px',
    fontWeight: '500',
    mb: '24px'
  }
};

const AdminMaterialEdit: React.FC = () => {
  const navigate = useNavigate();
  const editFlg = window.location.href.includes('edit');
  const location = useLocation();

  const showMessageList = [
    {
      option: 'ホーム画面のメッセージBOXにCO2基準値を変更した旨を表示する',
      value: 1
    }
  ];

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue
  } = useForm<MaterialType>({
    defaultValues: {
      showMessage: Array(showMessageList.length).fill(false)
    },
    resolver: yupResolver(materialSchema)
  });

  const buttonDisabledFlag = (): boolean => {
    return (
      watch('name') === undefined ||
      '' ||
      watch('category') === undefined ||
      '' ||
      watch('co2Default') === undefined ||
      '' ||
      watch('co2Unit') === undefined ||
      '' ||
      watch('sort') === undefined ||
      '' ||
      Boolean(errors.name) ||
      Boolean(errors.category) ||
      Boolean(errors.co2Default) ||
      Boolean(errors.co2Unit) ||
      Boolean(errors.costUnit) ||
      Boolean(errors.co2Standard) ||
      Boolean(errors.priceCondition) ||
      Boolean(errors.note) ||
      Boolean(errors.sort) ||
      Boolean(errors.showMessage)
    );
  };

  // 画面のアクセス制御
  const { isLoggedIn, setSnackbarOpen, setErrorText, setInfoText } =
    useContext(AuthContext);
  const currentUserType = Number(localStorage.getItem('user_type'));
  const [categoryList, setCategoryList] = useState<
    { value: number; option: string }[]
  >([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        if (!isLoggedIn) return navigate('/auth/login');
        // 事務局画面で事務局ユーザーではない場合は、ホームに飛ばす
        if (currentUserType !== userType.admin) return navigate('/home');
        const categories = await getCategoryList();
        const categoriesList = categories.data.map(
          (category: { id: number; name: string }) => {
            return { value: category.id, option: category.name };
          }
        );
        if (editFlg) {
          const data = location?.state?.data;
          if (data) {
            setValue('name', data.name);
            setValue('category', data.categoryId);
            setValue('co2Default', data.standard);
            setValue('co2Unit', data.co2Unit);
            setValue('costUnit', data.costUnit);
            setValue('co2Standard', data.calculation);
            setValue('priceCondition', data.priceCondition);
            setValue('note', data.remark);
            setValue('sort', data.sortNo);
            setValue('showMessage', Array(showMessageList.length).fill(false));
          }
        }
        setCategoryList(categoriesList);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [isLoggedIn, currentUserType]);

  const onSubmit = async () => {
    try {
      setLoading(true);
      if (editFlg) {
        const data = location?.state?.data;
        const material = await updateMaterial(
          data.id,
          watch('name'),
          watch('co2Default'),
          watch('co2Unit'),
          watch('costUnit'),
          watch('co2Standard'),
          watch('priceCondition'),
          watch('note'),
          watch('sort'),
          watch('category'),
          watch('showMessage')[0] === true
            ? showMessageEnum.show
            : showMessageEnum.hide
        );
        if (material) {
          navigate('/operator/materials/complete', {
            state: { status: 'edit' }
          });
        } else {
          throw new Error();
        }
      } else {
        const material = await createMaterial(
          watch('name'),
          watch('co2Default'),
          watch('co2Unit'),
          watch('costUnit'),
          watch('co2Standard'),
          watch('priceCondition'),
          watch('note'),
          watch('sort'),
          watch('category'),
          watch('showMessage')[0] === true
            ? showMessageEnum.show
            : showMessageEnum.hide
        );
        if (material) {
          navigate('/operator/materials/complete', {
            state: { status: 'new' }
          });
        } else {
          throw new Error();
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const errorResponse = axiosError.response as response;
        if (setErrorText && setSnackbarOpen && setInfoText) {
          setErrorText(errorResponse.data.message);
          setSnackbarOpen(true);
          setInfoText('');
        }
      } else {
        if (setErrorText && setSnackbarOpen && setInfoText) {
          setErrorText(common);
          setSnackbarOpen(true);
          setInfoText('');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container justifyContent="center" alignItems="center">
      <CustomLoading loading={loading} />
      <SideBar />
      <Grid container sx={styles.container} justifyContent={'center'}>
        <Grid item sx={styles.body} xs>
          <Typography sx={styles.title}>
            {editFlg ? '部材編集' : '部材追加'}
          </Typography>
          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.name)}
                formMb="24px"
                helperText={errors.name?.message}
                labelText="部材名"
                placeholder="コンクリート"
                requiredText="必須"
              />
            )}
            control={control}
            name="name"
          />
          <Controller
            render={({ field }) => (
              <CustomSelectField
                {...field}
                labelText="大分類"
                error={Boolean(errors.category)}
                helperText={errors.category?.message}
                requiredText="必須"
                formMb="24px"
                placeholder="大分類を選択"
                defaultValue={0}
                width="560px"
                list={categoryList}
              />
            )}
            control={control}
            name="category"
          />
          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.co2Default)}
                formMb="24px"
                helperText={errors.co2Default?.message}
                labelText="CO2基準値のデフォルト値"
                placeholder="xxxx"
                requiredText="必須"
              />
            )}
            control={control}
            name="co2Default"
          />
          <Typography sx={{ display: 'flex' }}>
            <Typography sx={{ mr: '26px' }}>
              <Typography sx={{ display: 'flex', mb: '8px' }}>
                <Typography sx={{ color: black, fontSize: '14px', mr: '8px' }}>
                  CO2単位
                </Typography>
                <Typography sx={{ color: errorColor, fontSize: '14px' }}>
                  必須
                </Typography>
              </Typography>
              <Grid container alignItems="flex-end" flexWrap="nowrap">
                <Typography
                  sx={{
                    fontSize: '16px',
                    mr: '5px',
                    mb: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    alignSelf: 'center',
                    whiteSpace: 'nowrap'
                  }}
                >
                  kg-CO2/
                </Typography>
                <Controller
                  name="co2Unit"
                  control={control}
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="xxxx"
                      formMb="24px"
                      width="193px"
                      error={Boolean(errors.co2Unit)}
                      helperText={errors.co2Unit?.message}
                    />
                  )}
                />
              </Grid>
            </Typography>
            <Typography>
              <Typography sx={{ display: 'flex', mb: '8px' }}>
                <Typography sx={{ color: black, fontSize: '14px', mr: '8px' }}>
                  単価単位
                </Typography>
              </Typography>
              <Grid container alignItems="flex-end" flexWrap="nowrap">
                <Typography
                  sx={{
                    fontSize: '16px',
                    mr: '5px',
                    mb: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    alignSelf: 'center'
                  }}
                >
                  円/
                </Typography>
                <Controller
                  name="costUnit"
                  control={control}
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="xxxx"
                      formMb="24px"
                      width="243px"
                      error={Boolean(errors.costUnit)}
                      helperText={errors.costUnit?.message}
                    />
                  )}
                />
              </Grid>
            </Typography>
          </Typography>

          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.co2Standard)}
                formMb="24px"
                helperText={errors.co2Standard?.message}
                fieldType="textareaRow2"
                multiline
                labelText="基準CO2算定仕様"
                placeholder="サンプルテキスト"
                rows={2}
              />
            )}
            control={control}
            name="co2Standard"
          />
          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.priceCondition)}
                formMb="24px"
                helperText={errors.priceCondition?.message}
                fieldType="textareaRow2"
                multiline
                labelText="単価算定条件"
                placeholder="サンプルテキスト"
                rows={2}
              />
            )}
            control={control}
            name="priceCondition"
          />
          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.note)}
                formMb="24px"
                helperText={errors.note?.message}
                fieldType="textareaRow2"
                multiline
                labelText="備考"
                placeholder="サンプルテキスト"
                rows={2}
              />
            )}
            control={control}
            name="note"
          />
          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.sort)}
                formMb="24px"
                helperText={errors.sort?.message}
                labelText="ソート順"
                placeholder="1"
                requiredText="必須"
              />
            )}
            control={control}
            name="sort"
          />
          <Controller
            render={({ field }) => (
              <CustomCheckBox
                {...field}
                width={'560px'}
                list={showMessageList}
                value={field.value}
                onChange={field.onChange}
                error={Boolean(errors.showMessage)}
                helperText={errors.showMessage?.message}
              />
            )}
            control={control}
            name="showMessage"
          />
          <Button
            disabled={buttonDisabledFlag()}
            fullWidth
            sx={[
              primaryButtonBackgroundColor,
              {
                height: '44px',
                width: '560px',
                marginTop: '45px',
                '@media screen and (max-width:600px)': {
                  width: '100%'
                }
              }
            ]}
            variant="contained"
            onClick={() => {
              handleSubmit(onSubmit)();
            }}
          >
            <Typography color="white" component="div" sx={{ fontSize: '16px' }}>
              {editFlg ? '変更する' : '追加する'}
            </Typography>
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AdminMaterialEdit;
